import * as React from "react";
import type {
  AppContextMenuProps,
  AppContextMenuState,
  ExtendedPositioningProps,
} from "./AppContextualMenu.types";
import {
  useFocusFinders,
  useMenu_unstable,
  makeStyles,
} from "@fluentui/react-components";
import type { AppMenuItemProps } from "../AppMenuItem";
import { useAsyncReactUITiming } from "../../hooks/useAsyncReactUITimingEnabled";
import { useMenuPositioning } from "../../hooks/useMenuPositioning";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { useV9MenuAutoDismissFixEnabled } from "../../hooks/useNextComponentsEnabled";
import { useDispatchOptimizationEnabled } from "../../hooks/useNextComponentsEnabled";
import { performOnNextFrame } from "@1js/acui-common";

const useHiddenMenuStyle = makeStyles({
  root: {
    visibility: "hidden",
    position: "absolute",
  },
});

export const useAppContextualMenu = (
  props: AppContextMenuProps
): AppContextMenuState => {
  const menuItems = props.items as AppMenuItemProps[];
  const isAsyncReactUITimingEnabled = useAsyncReactUITiming();
  const appContext = React.useContext(AppContext);
  const isV9FileMenuDismissOnTargetButtonClickFixEnabled = checkExperiment(
    appContext,
    ribbonExperiments.isV9FileMenuDismissOnButtonClickFixEnabled
  );
  const isV9MenuAutoDismissFixEnabled = useV9MenuAutoDismissFixEnabled();
  const isDispatchOptimizationEnabled = useDispatchOptimizationEnabled();
  const listRef = React.useRef<HTMLDivElement>(null);

  // Hook to find the first focusable element in the Menu.
  const { findFirstFocusable } = useFocusFinders();

  const state = useMenu_unstable({
    ...props,
    open: props.hidden ? false : true,
    closeOnScroll: false,
  }) as AppContextMenuState;

  const hiddenMenuStyle = useHiddenMenuStyle();

  React.useEffect(() => {
    if (!props.hidden && listRef.current && isDispatchOptimizationEnabled) {
      // In V9 menu, we move focus on render. Now that we do not unmount the menu, it will not move focus again.
      // Added a use effect that is applied when the hidden state changes to focus on the menu.
      performOnNextFrame(() => {
        const firstItem = findFirstFocusable(listRef.current!);
        firstItem?.focus();
      });
    }
  }, [props.hidden, findFirstFocusable, isDispatchOptimizationEnabled]);

  return {
    ...props,
    ...state,
    isAsyncReactUITimingEnabled,
    appMenuItems: menuItems,
    appMenuPopoverProps: {
      id: props.id,
      ref: props.contextMenuRef
        ? (props.contextMenuRef as React.RefObject<HTMLDivElement>)
        : undefined,
      className: props.className,
    },
    appMenuListProps: {},
    positioning: {
      positioningRef: useMenuPositioning(props),
      unstable_disableTether: "all",
    } as unknown as ExtendedPositioningProps,
    preventDismissOnTargetClick:
      isV9FileMenuDismissOnTargetButtonClickFixEnabled,
    listRef,
    findFirstFocusable,
    isV9MenuAutoDismissFixEnabled,
    hiddenMenuStyle,
    isDispatchOptimizationEnabled,
  };
};
